import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";

// 2026-10-22: settings of an existing search in the Mini App (name, link, price, seller, words).
// Saved through PATCH /searches/{id}; only the fields shown here are sent, the rest stay as they are.

function words(s: string): string[] | null {
  const list = s.split(",").map((x) => x.trim()).filter(Boolean);
  return list.length ? list : null;
}

export default function EditSearch() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const plan: any = (user as any)?.active_subscription?.plan;
  const isAdmin = !!(user as any)?.is_admin;
  const canSeller = isAdmin || !!plan?.seller_filter;
  const canAdvanced = isAdmin || !!plan?.advanced_filters;

  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [origUrl, setOrigUrl] = useState("");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [sellerType, setSellerType] = useState("ANY");
  const [hideFrequent, setHideFrequent] = useState(false);
  const [include, setInclude] = useState("");
  const [exclude, setExclude] = useState("");

  useEffect(() => {
    api.get(`/searches/${id}`)
      .then((r: any) => {
        const s = r.data;
        const f = s.filters || {};
        setName(s.name || "");
        setUrl(s.url || "");
        setOrigUrl(s.url || "");
        setPriceMin(f.price_min != null ? String(f.price_min) : "");
        setPriceMax(f.price_max != null ? String(f.price_max) : "");
        setSellerType(f.seller_type || "ANY");
        setHideFrequent(!!f.hide_frequent_sellers);
        setInclude((f.include_keywords || []).join(", "));
        setExclude((f.exclude_keywords || []).join(", "));
        setLoaded(true);
      })
      .catch(() => {
        alert("Поиск не найден");
        navigate("/searches");
      });
  }, [id]);

  const save = async () => {
    const urlChanged = url.trim() !== origUrl;
    if (urlChanged && !confirm("Ссылка изменится — поиск начнёт заново: старые объявления по новой ссылке не придут, только новые. Продолжить?")) return;
    const filters: any = {
      price_min: priceMin ? parseInt(priceMin, 10) : null,
      price_max: priceMax ? parseInt(priceMax, 10) : null,
    };
    if (canSeller) {
      filters.seller_type = sellerType;
      filters.hide_frequent_sellers = hideFrequent;
    }
    if (canAdvanced) {
      filters.include_keywords = words(include);
      filters.exclude_keywords = words(exclude);
    }
    const body: any = { name: name.trim() || "Поиск", filters };
    if (urlChanged) body.url = url.trim();
    setSaving(true);
    try {
      await api.patch(`/searches/${id}`, body);
      navigate("/searches");
    } catch (e: any) {
      alert("Ошибка: " + (e.response?.data?.detail || "не удалось сохранить"));
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return <div style={styles.container}><div style={styles.hint}>Загрузка…</div></div>;

  return (
    <div style={styles.container}>
      <button onClick={() => navigate("/searches")} style={styles.back}>← Мои поиски</button>
      <h1 style={styles.title}>⚙️ Настройки поиска</h1>

      <label style={styles.label}>Название</label>
      <input style={styles.input} value={name} onChange={(e) => setName(e.target.value)} />

      <label style={styles.label}>Ссылка на Avito</label>
      <input style={styles.input} type="url" value={url} onChange={(e) => setUrl(e.target.value)} />
      {url.trim() !== origUrl && <div style={styles.warn}>Новая ссылка = поиск начнётся заново</div>}

      <div style={styles.row}>
        <div style={{ flex: 1 }}>
          <label style={styles.label}>Цена от</label>
          <input style={styles.input} type="number" value={priceMin} onChange={(e) => setPriceMin(e.target.value)} placeholder="0" />
        </div>
        <div style={{ flex: 1 }}>
          <label style={styles.label}>Цена до</label>
          <input style={styles.input} type="number" value={priceMax} onChange={(e) => setPriceMax(e.target.value)} placeholder="∞" />
        </div>
      </div>

      {canSeller ? (
        <>
          <label style={styles.label}>Тип продавца</label>
          <select style={styles.input} value={sellerType} onChange={(e) => setSellerType(e.target.value)}>
            <option value="ANY">Любой</option>
            <option value="PRIVATE">Только частные</option>
            <option value="BUSINESS">Только магазины</option>
          </select>
          <div style={styles.hint}>Магазин — тот, кого Авито отмечает «Реквизиты проверены» или «Компания».</div>
          <label style={styles.check}>
            <input type="checkbox" checked={hideFrequent} onChange={(e) => setHideFrequent(e.target.checked)} />
            <span>Скрывать частых продавцов (магазины и тех, у кого 3+ объявления на странице)</span>
          </label>
        </>
      ) : (
        <div style={styles.upsell}>Фильтр продавцов — на тарифах PRO и EXPERT</div>
      )}

      {canAdvanced ? (
        <>
          <label style={styles.label}>Включить слова (через запятую)</label>
          <input style={styles.input} value={include} onChange={(e) => setInclude(e.target.value)} placeholder="pro max, 256" />
          <label style={styles.label}>Исключить слова (через запятую)</label>
          <input style={styles.input} value={exclude} onChange={(e) => setExclude(e.target.value)} placeholder="ремонт, разбит, запчасти" />
        </>
      ) : (
        <div style={styles.upsell}>Фильтр по словам — на тарифе EXPERT</div>
      )}

      <button style={{ ...styles.save, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={save}>
        {saving ? "Сохраняю…" : "Сохранить"}
      </button>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { padding: "16px", maxWidth: "480px", margin: "0 auto" },
  back: { background: "none", border: "none", color: "var(--tg-theme-link-color, #2481cc)", fontSize: "14px", cursor: "pointer", padding: 0, marginBottom: "12px" },
  title: { fontSize: "22px", fontWeight: 700, margin: "0 0 12px 0", color: "var(--tg-theme-text-color, #000)" },
  label: { display: "block", fontSize: "13px", fontWeight: 600, margin: "12px 0 6px 0", color: "var(--tg-theme-text-color, #000)" },
  input: { width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: "10px", border: "1px solid var(--tg-theme-hint-color, #ccc)", fontSize: "15px", backgroundColor: "var(--tg-theme-bg-color, #fff)", color: "var(--tg-theme-text-color, #000)" },
  row: { display: "flex", gap: "10px" },
  hint: { fontSize: "12px", color: "var(--tg-theme-hint-color, #666)", marginTop: "6px" },
  warn: { fontSize: "12px", color: "#d97706", marginTop: "6px" },
  check: { display: "flex", gap: "8px", alignItems: "flex-start", fontSize: "14px", margin: "12px 0 0 0", color: "var(--tg-theme-text-color, #000)" },
  upsell: { fontSize: "12px", color: "var(--tg-theme-hint-color, #666)", backgroundColor: "var(--tg-theme-secondary-bg-color, #f3f4f6)", borderRadius: "10px", padding: "10px", marginTop: "12px" },
  save: { width: "100%", marginTop: "20px", padding: "14px", borderRadius: "12px", border: "none", fontSize: "16px", fontWeight: 600, cursor: "pointer", backgroundColor: "var(--tg-theme-button-color, #2481cc)", color: "var(--tg-theme-button-text-color, #fff)" },
};
